import { Injectable } from '@angular/core';

import { CompoundFilter } from '@itonics/filters';

import { LogService } from '@innovation-os-ng/core/logger';
import { iptc } from '../../../../shared/dictionaries/iptc';
import { flatHierarchical } from '../../../helpers/flat-hierarchical';
import { normalize } from '../../../helpers/normalize-helper';
import { recursion } from '../../../helpers/recursion';
import { ASYNC_METRIC_OPTIONS, MetricOptions, MetricsAggregation } from '../../../model/metric';
import { DocumentsTopic, DocumentsTopicsPostBody, DocumentsTopicsResult, ExecutionDetailAsync } from '../../../model/types';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class AggregateTopicsService {
  private topicDictionary: {
    [key: string]: {
      label: string;
      parent: string;
      id: string;
    };
  } = {};

  constructor(private readonly apiService: ApiService, private readonly logger: LogService) {
    iptc.forEach((topic) => {
      this.topicDictionary[topic.id] = topic;
    });
  }

  /**
   * Fetch the topics aggregations
   *
   * @param query filter to apply
   * @param metricOptions what metric and metric function to use to calculate values
   * @param id If provided will replace all pending previous calls with the same ID
   * @param topicSource type of topics, could be categories (hierarchical) or topics
   * @returns Promise<DocumentsTopicsResult>
   */
  public getAggregateTopics(query: CompoundFilter, metricOptions: MetricOptions, id?: string, topicSource?: string): any {
    // Certain metric options should be requested asynchronously because calculating
    // their results will otherwise cause a synchronous request to timeout
    const async = ASYNC_METRIC_OPTIONS.indexOf(metricOptions?.metric) !== -1;

    return new Promise((resolve, reject) => {
      const postBody: DocumentsTopicsPostBody = {
        topicSource: topicSource ? topicSource : 'iptcCategories',
        query: query.toJson(true),
        ...metricOptions,
        executionDetails: async ? { asynchronous: ExecutionDetailAsync.Aggregations } : null
      };

      // If the metric is not document count we want to normalize all the values from 0 - 100
      const normalizeAndResolve = (response: DocumentsTopicsResult) => {
        if (metricOptions.metric !== MetricsAggregation.COUNT) {
          this.normalize(response);
        }
        resolve(response);
      };

      this.apiService.postDocumentsTopics(postBody, async ? `async-${id}` : id).then(
        (response: DocumentsTopicsResult) => normalizeAndResolve(response),
        (error) => {
          this.logger.warn('There was an error', { error });
        }
      );
    });
  }

  private normalize(response: DocumentsTopicsResult) {
    // We need to flat the hierarchical data to extract the Max number
    const max = Math.max(...flatHierarchical({ children: response.topics }).map((d: DocumentsTopic) => d.value));

    recursion(response.topics, (item) => {
      const normalized = normalize(0, max, item.value);
      item.value = normalized;
      item.doc_count = normalized;
    });
  }
}
